import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSidebar } from "./AppLayout";
import { cn } from "@/lib/utils";

// Feature flag for new application functionality
const FEATURE_NEW_APPLICATION = import.meta.env.VITE_FEATURE_NEW_APPLICATION === "true";

function PanelLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function getAuthTypeLabel(authType?: string): string {
  const labels: Record<string, string> = {
    dig_sign: "КЕП",
    diia_id: "Дія.Підпис",
    bank_id: "BankID",
    diia_oauth: "Дія",
  };
  return labels[authType || ""] || "";
}

export function Header() {
  const { user, logout } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initials = user?.givenname
    ? `${user.givenname.charAt(0)}${user.lastname?.charAt(0) || ""}`.toUpperCase()
    : "?";

  return (
    <header className="h-16 bg-white border-b-[3px] border-border/50 sticky top-0 z-30">
      <div className="h-full flex items-center justify-between">
        {/* Left - Toggle (same width as collapsed sidebar) */}
        <div className="h-full w-16 flex items-center justify-center shrink-0">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="h-10 w-10 rounded-xl flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-muted transition-all"
          >
            <PanelLeftIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Right */}
        <div className="h-full flex items-center">
          {/* Notifications - only shown when feature is enabled */}
          {FEATURE_NEW_APPLICATION && (
            <>
              <button className="relative h-full w-16 flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-muted/50 transition-all">
                <BellIcon className="h-5 w-5" />
                <span className="absolute top-4 right-5 h-2 w-2 rounded-full bg-primary ring-2 ring-white" />
              </button>

              {/* Divider */}
              <div className="h-8 w-px bg-border/50" />
            </>
          )}

          {/* User Menu */}
          <div className="relative h-full" ref={ref}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "h-full w-16 flex items-center justify-center transition-all",
                isOpen ? "bg-muted/50" : "hover:bg-muted/50"
              )}
            >
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                <span className="text-primary-foreground text-sm font-semibold">{initials}</span>
              </div>
            </button>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute right-3 top-full mt-1 w-64 rounded-xl border-[3px] border-border/50 bg-white shadow-lg overflow-hidden z-50">
              <div className="px-3 py-3 bg-muted/30 border-b border-border/50">
                <p className="text-[13px] font-semibold truncate" title={`${user?.givenname} ${user?.lastname}`}>
                  {user?.givenname} {user?.lastname}
                </p>
                {user?.email && (
                  <p className="text-[12px] text-muted-foreground truncate" title={user.email}>
                    {user.email}
                  </p>
                )}
                {user?.auth_type && (
                  <span className={cn("inline-block px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded-md", user?.email ? "mt-1.5" : "mt-1")}>
                    {getAuthTypeLabel(user.auth_type)}
                  </span>
                )}
              </div>

              <div className="p-1.5">
                <Link
                  to="/settings"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2.5 px-2.5 py-2 text-[13px] rounded-lg hover:bg-muted transition-colors"
                >
                  <SettingsIcon className="h-4 w-4 text-muted-foreground" />
                  Налаштування
                </Link>
              </div>

              <div className="p-1.5 border-t">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 text-[13px] rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <LogoutIcon className="h-4 w-4" />
                  Вийти
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </header>
  );
}
